import type { ReportLogicContext } from "./context";
import type { CategorizationRule, DataColumn, MetricSection, ReportConfig, RuleCondition, RuleNode, RuleSet, TripDataFieldDef, TripDataFieldState } from "../types";
import { findReportColumn, tripDataColumnForKey, columnsForModel, TRIP_DATA_FIELDS } from "./catalogResolution";
import { isNativeDateColumn, minutesBetweenSql, nullFallbackNote, sqlLiteral, todayPhrase } from "./sqlExpressions";
import { isRelatedColumn, relatedValueSql } from "./relatedTables";

/** " (blank counts as 12-31-2199)" when the condition's fallback applies (mirrors NullFallbackNote). */
function blankDateNote(column: DataColumn, condition: RuleCondition) {
    return nullFallbackNote(condition, column);
}
import { metricFilterLogic, metricFilterNodes, metricFilterScope, ruleConditionSql, ruleHasResult, ruleResultLabel, ruleSetModelKey, ruleValueColumn, ruleValueFromColumn, ruleValueNotBlankCondition } from "./ruleInterpretation";
import { isRuleGroupNode, MAX_RULE_GROUP_DEPTH, ruleNodeJoin, splitRuleAndRuns } from "./rules";
import { resolveDifferenceUnit } from "./temporalPrecision";
import { tripDataFieldMatchExpr } from "./sqlPreview";

// Preview of the per-row "Reason Details" in the Excluded Records download. Mirrors
// Workbook/ReportSqlBuilder.ExclusionReasons.cs: condition trees are walked with the same
// algorithm as ruleNodesExpression, and next to each boolean we build a text expression that
// names the part the row failed (or, for "leave out" filters, matched) with the row's values:
//
//   explain-failure:  leaf -> "<column> = <row value> (needed: <requirement>)"
//                     AND  -> the members that are not TRUE
//                     OR   -> "none matched: [run 1] OR [run 2]"
//   explain-match:    leaf -> "<column> = <row value> (excluded: <condition>)"
//                     AND  -> every member; OR -> the runs that are TRUE
//
// As in the other previews, configured values appear as literals; the server binds them.

export interface ExplainedExpr {
    bool: string;
    fail: string;
    match: string;
}

export interface ExplainedCondition {
    label: string;
    expr: string;
    detail: string;
    // Plain English, phrased to follow "A record is excluded when ..." (mirrors
    // ReportSqlBuilder.ExclusionEnglish.cs).
    english?: string;
}

const VALUE_LIST_PREVIEW = 10;

function stuffJoin(parts: string[]) {
    return `STUFF(CONCAT(${parts.join(",\n ")}, N''), 1, 2, N'')`;
}

function nLiteral(text: string) {
    return `N${sqlLiteral(text)}`;
}

function andRun(members: ExplainedExpr[]): ExplainedExpr {
    if (members.length === 1) return members[0];
    return {
        bool: `(${members.map((m) => m.bool).join(" AND ")})`,
        fail: stuffJoin(members.map((m) => `CASE WHEN ${m.bool}\n THEN N'' ELSE CONCAT(N'; ', ${m.fail}) END`)),
        match: `CONCAT(${members.map((m) => m.match).join(", N' AND ', ")})`,
    };
}

function orLevel(runs: ExplainedExpr[]): ExplainedExpr {
    if (runs.length === 1) return runs[0];
    const failParts = ["N'none matched: ['"];
    runs.forEach((run, index) => {
        if (index > 0) failParts.push("N'] OR ['");
        failParts.push(run.fail);
    });
    failParts.push("N']'");
    return {
        bool: `(${runs.map((r) => r.bool).join(" OR ")})`,
        fail: `CONCAT(${failParts.join(", ")})`,
        match: stuffJoin(runs.map((r) => `CASE WHEN ${r.bool}\n THEN CONCAT(N'; ', ${r.match}) ELSE N'' END`)),
    };
}

function columnLabel(column: DataColumn) {
    return (column.label || column.key || "").trim();
}

function valueList(values: string[], op = "in") {
    if (op === "between" || op === "not_between") return values.slice(0, 2).join(" and ");
    const shown = values.slice(0, VALUE_LIST_PREVIEW).join(", ");
    return values.length > VALUE_LIST_PREVIEW ? `${shown} (+${values.length - VALUE_LIST_PREVIEW} more)` : shown;
}

function rowValueSql(column: DataColumn) {
    // A related column is the record's value list (logic/relatedTables.ts).
    if (isRelatedColumn(column)) return `ISNULL(NULLIF(LTRIM(RTRIM(LEFT(${relatedValueSql(column)}, 200))), N''), N'(blank)')`;
    const text = isNativeDateColumn(column)
        ? (String(column.dataType || "").trim().toLowerCase() === "date"
            ? `CONVERT(NVARCHAR(10), ${column.sql}, 110)`
            : `CONCAT(CONVERT(NVARCHAR(10), ${column.sql}, 110), N' ', CONVERT(NVARCHAR(5), ${column.sql}, 108))`)
        : `LEFT(CONVERT(NVARCHAR(4000), ${column.sql}), 200)`;
    return `ISNULL(NULLIF(LTRIM(RTRIM(${text})), N''), N'(blank)')`;
}

function labelEquals(column: DataColumn) {
    return nLiteral(`${columnLabel(column)} = `);
}

function valuePhrase(op: string, values: string[], text?: string) {
    switch (op) {
        case "in": return `one of: ${valueList(values, op)}`;
        case "not_in": return `not one of: ${valueList(values, op)}`;
        case "eq": return `equal to ${values[0] ?? ""}`;
        case "ne": return `not equal to ${values[0] ?? ""}`;
        case "contains": return `contains '${text ?? ""}'`;
        case "not_contains": return `does not contain '${text ?? ""}'`;
        case "starts_with": return `starts with '${text ?? ""}'`;
        case "not_starts_with": return `does not start with '${text ?? ""}'`;
        case "ends_with": return `ends with '${text ?? ""}'`;
        case "not_ends_with": return `does not end with '${text ?? ""}'`;
        case "gt": return `greater than ${values[0] ?? ""}`;
        case "gte": return `at least ${values[0] ?? ""}`;
        case "lt": return `less than ${values[0] ?? ""}`;
        case "lte": return `at most ${values[0] ?? ""}`;
        case "after": return `after ${values[0] ?? ""}`;
        case "before": return `before ${values[0] ?? ""}`;
        case "between": return `between ${values[0] ?? ""} and ${values[1] ?? ""}`;
        case "not_between": return `not between ${values[0] ?? ""} and ${values[1] ?? ""}`;
        case "empty": return "blank";
        case "not_empty": return "not blank";
        default: return `${op.replace(/_/g, " ")} ${values[0] ?? ""}`.trim();
    }
}

/** The requirement, phrased to follow "needed:" / "excluded:" (mirrors RulePhrase). */
function rulePhrase(column: DataColumn, compare: DataColumn | null, condition: RuleCondition) {
    const op = String(condition.operator || "in").trim().toLowerCase();
    const values = (condition.values || []).filter((value) => value !== "" && value != null);
    if (compare && condition.leftKind === "minutes_between") {
        const unit = resolveDifferenceUnit(column, compare, condition.differenceUnit);
        return `${unit}s between ${columnLabel(column)} and ${columnLabel(compare)}${condition.absoluteMinutes ? " (absolute)" : ""} ${valuePhrase(op, values)}`.trim();
    }
    if (compare) return `${columnLabel(column)} ${valuePhrase(op, [columnLabel(compare)])}${blankDateNote(column, condition)}`;
    if (condition.compareTo === "today") return `${todayPhrase(op, "bare")}${blankDateNote(column, condition)}`;
    return `${valuePhrase(op, values, (condition.text || "").trim())}${blankDateNote(column, condition)}`;
}

function rowEquals(column: DataColumn) {
    return `CONCAT(${labelEquals(column)}, ${rowValueSql(column)})`;
}

// "<row values> (needed: <rule>)" / "<row values> (excluded: <rule>)"
function neededText(rowValues: string, rule: string) {
    return `CONCAT(${rowValues}, N' (needed: ', ${nLiteral(rule)}, N')')`;
}

function excludedText(rowValues: string, rule: string) {
    return `CONCAT(${rowValues}, N' (excluded: ', ${nLiteral(rule)}, N')')`;
}

function explainLeaf(context: ReportLogicContext, report: ReportConfig, condition: RuleCondition, useIds: boolean): ExplainedExpr | null {
    const sql = ruleConditionSql(context, report, condition, useIds);
    if (!sql) return null;
    const column = findReportColumn(context, report, condition.column) as DataColumn;
    const compare = (condition.leftKind === "minutes_between" || condition.compareTo === "column") && condition.compareColumn
        ? (findReportColumn(context, report, condition.compareColumn) as DataColumn | undefined) || null
        : null;
    let rowValues: string;
    if (compare && condition.leftKind === "minutes_between" && !isRelatedColumn(column) && !isRelatedColumn(compare)) {
        const unit = resolveDifferenceUnit(column, compare, condition.differenceUnit);
        const diff = minutesBetweenSql(column, compare, condition.absoluteMinutes, condition.differenceUnit);
        rowValues = `CONCAT(${labelEquals(column)}, ${rowValueSql(column)}, N', ', ${labelEquals(compare)}, ${rowValueSql(compare)}, N', difference = ', ISNULL(CONVERT(NVARCHAR(40), ${diff}), N'(blank)'), N' ${unit}(s)')`;
    } else if (compare) {
        rowValues = `CONCAT(${labelEquals(column)}, ${rowValueSql(column)}, N', ', ${labelEquals(compare)}, ${rowValueSql(compare)})`;
    } else {
        rowValues = rowEquals(column);
    }
    const rule = rulePhrase(column, compare, condition);
    return { bool: sql, fail: neededText(rowValues, rule), match: excludedText(rowValues, rule) };
}

function explainNodes(context: ReportLogicContext, report: ReportConfig, nodes: RuleNode[] | null | undefined, logic: "and" | "or", useIds: boolean, depth = 0): ExplainedExpr | null {
    if (depth > MAX_RULE_GROUP_DEPTH) return null;
    const items = (nodes || [])
        .map((node) => {
            const explained = isRuleGroupNode(node)
                ? explainNodes(context, report, node.conditions, node.conditionLogic, useIds, depth + 1)
                : explainLeaf(context, report, node as RuleCondition, useIds);
            return explained ? { value: explained, join: ruleNodeJoin(node, logic) } : null;
        })
        .filter(Boolean) as { value: ExplainedExpr; join: "and" | "or" }[];
    if (!items.length) return null;
    return orLevel(splitRuleAndRuns(items).map(andRun));
}

// ---- plain English (mirrors ReportSqlBuilder.ExclusionEnglish.cs) ----

const ENGLISH_VALUE_LIST_PREVIEW = 25;

interface EnglishExpr { text: string; isOr?: boolean; isAnd?: boolean }

function englishValue(value: string, column?: DataColumn | null) {
    if (column && isNativeDateColumn(column)) {
        const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value).trim());
        if (match) return `${match[2]}-${match[3]}-${match[1]}`;
    }
    return value;
}

export function englishList(values: string[]) {
    if (!values.length) return "";
    if (values.length > ENGLISH_VALUE_LIST_PREVIEW) {
        return `${values.slice(0, ENGLISH_VALUE_LIST_PREVIEW).join(", ")} and ${values.length - ENGLISH_VALUE_LIST_PREVIEW} more`;
    }
    return values.length === 1 ? values[0] : `${values.slice(0, -1).join(", ")} or ${values[values.length - 1]}`;
}

export function englishPhrase(op: string, values: string[], text?: string | null, column?: DataColumn | null) {
    const shown = values.map((value) => englishValue(value, column));
    const first = shown[0] ?? "";
    switch (op) {
        case "in": return shown.length === 1 ? `is ${first}` : `is one of ${englishList(shown)}`;
        case "not_in": return shown.length === 1 ? `is not ${first}` : `is not one of ${englishList(shown)}`;
        case "eq": return `is ${first}`;
        case "ne": return `is not ${first}`;
        case "contains": return `contains "${text ?? ""}"`;
        case "not_contains": return `does not contain "${text ?? ""}"`;
        case "starts_with": return `starts with "${text ?? ""}"`;
        case "not_starts_with": return `does not start with "${text ?? ""}"`;
        case "ends_with": return `ends with "${text ?? ""}"`;
        case "not_ends_with": return `does not end with "${text ?? ""}"`;
        case "gt": return `is greater than ${first}`;
        case "gte": return `is ${first} or more`;
        case "lt": return `is less than ${first}`;
        case "lte": return `is ${first} or less`;
        case "after": return `is after ${first}`;
        case "before": return `is before ${first}`;
        case "between": return `is between ${shown[0] ?? ""} and ${shown[1] ?? ""}`;
        case "not_between": return `is not between ${shown[0] ?? ""} and ${shown[1] ?? ""}`;
        case "empty": return "is blank";
        case "not_empty": return "is not blank";
        default: return `${op.replace(/_/g, " ")} ${first}`.trim();
    }
}

function englishLeaf(context: ReportLogicContext, report: ReportConfig, condition: RuleCondition): EnglishExpr | null {
    if (!ruleConditionSql(context, report, condition, false)) return null;
    const column = findReportColumn(context, report, condition.column) as DataColumn;
    const compare = (condition.leftKind === "minutes_between" || condition.compareTo === "column") && condition.compareColumn
        ? (findReportColumn(context, report, condition.compareColumn) as DataColumn | undefined) || null
        : null;
    const op = String(condition.operator || "in").trim().toLowerCase();
    const values = (condition.values || []).filter((value) => value !== "" && value != null);
    if (compare && condition.leftKind === "minutes_between") {
        const unit = resolveDifferenceUnit(column, compare, condition.differenceUnit);
        return { text: `the ${condition.absoluteMinutes ? "absolute " : ""}number of ${unit}s between ${columnLabel(column)} and ${columnLabel(compare)} ${englishPhrase(op, values)}` };
    }
    const subject = `${columnLabel(column)}${blankDateNote(column, condition)}`;
    if (compare) return { text: `${subject} ${englishPhrase(op, [columnLabel(compare)])}` };
    if (condition.compareTo === "today") return { text: `${subject} ${todayPhrase(op, "is")}` };
    return { text: `${subject} ${englishPhrase(op, values, (condition.text || "").trim(), column)}` };
}

function englishNodes(context: ReportLogicContext, report: ReportConfig, nodes: RuleNode[] | null | undefined, logic: "and" | "or", depth = 0): EnglishExpr | null {
    if (depth > MAX_RULE_GROUP_DEPTH) return null;
    const items = (nodes || [])
        .map((node) => {
            const english = isRuleGroupNode(node)
                ? englishNodes(context, report, node.conditions, node.conditionLogic, depth + 1)
                : englishLeaf(context, report, node as RuleCondition);
            return english ? { value: english, join: ruleNodeJoin(node, logic) } : null;
        })
        .filter(Boolean) as { value: EnglishExpr; join: "and" | "or" }[];
    if (!items.length) return null;
    const runs = splitRuleAndRuns(items).map((run) => run.length === 1
        ? run[0]
        : { text: run.map((m) => m.isOr ? `(${m.text})` : m.text).join(" and "), isAnd: true });
    return runs.length === 1 ? runs[0] : { text: runs.map((r) => r.isAnd ? `(${r.text})` : r.text).join(", or "), isOr: true };
}

export function englishExcludedWhen(sentences: string[]) {
    if (!sentences.length) return "Nothing is excluded: the metric has no conditions other than reporting period and health plan.";
    if (sentences.length === 1) return `A record is excluded when ${sentences[0]}.`;
    return `A record is excluded when any of these is true: ${sentences.map((s, i) => `(${i + 1}) ${s}`).join("; ")}.`;
}

/**
 * A Set rule's WHEN, explained - mirrors setRuleMatchExpression. A column-sourced rule also
 * needs its source column to have a value, AND-ed on as its own leaf ("needed: not blank").
 */
function explainSetRuleMatch(context: ReportLogicContext, report: ReportConfig, rule: CategorizationRule, useIds: boolean, modelKey: string): ExplainedExpr | null {
    const expr = explainNodes(context, report, rule.conditions, rule.conditionLogic, useIds);
    if (!expr || !ruleValueFromColumn(rule)) return expr;
    const column = ruleValueColumn(context, report, rule, modelKey);
    const guard = column ? explainLeaf(context, report, ruleValueNotBlankCondition(column), useIds) : null;
    return guard ? andRun([expr, guard]) : null;
}

/** "<conditions>" for a Set rule, plus "and <column> is not blank" when column-sourced. */
function englishSetRule(context: ReportLogicContext, report: ReportConfig, rule: CategorizationRule, modelKey: string) {
    const tree = englishNodes(context, report, rule.conditions, rule.conditionLogic);
    if (!tree || !ruleValueFromColumn(rule)) return tree ? tree.text : null;
    const column = ruleValueColumn(context, report, rule, modelKey);
    const guard = column ? englishLeaf(context, report, ruleValueNotBlankCondition(column)) : null;
    return guard ? `${tree.isOr ? `(${tree.text})` : tree.text} and ${guard.text}` : null;
}

/** Rule-set filter conditions with details, mirroring buildRuleFilterClauses. */
export function ruleSetExplainedConditions(context: ReportLogicContext, report: ReportConfig, ruleSet: RuleSet, useIds = false): ExplainedCondition[] {
    const name = (ruleSet.name || "").trim() || "Categorization";
    const includes: { number: number; expr: ExplainedExpr }[] = [];
    const excludes: { number: number; expr: ExplainedExpr }[] = [];
    const matchedSets: { value: string; expr: ExplainedExpr }[] = [];
    const modelKey = ruleSetModelKey(context, report, ruleSet);
    ruleSet.rules.forEach((rule, index) => {
        if (rule.action === "set") {
            if (ruleSet.includeElse === false && ruleHasResult(context, report, rule, modelKey)) {
                const expr = explainSetRuleMatch(context, report, rule, useIds, modelKey);
                if (expr) matchedSets.push({ value: ruleResultLabel(context, report, rule), expr });
            }
            return;
        }
        const expr = explainNodes(context, report, rule.conditions, rule.conditionLogic, useIds);
        if (!expr) return;
        if (rule.action === "include") includes.push({ number: index + 1, expr });
        if (rule.action === "exclude") excludes.push({ number: index + 1, expr });
    });

    const result: ExplainedCondition[] = [];
    if (includes.length) {
        result.push({
            label: `Rule set "${name}": did not match its include rule(s)`,
            english: `it matches none of the include rules of rule set "${name}": ${includes.map((item) => `rule ${item.number} (${englishNodes(context, report, ruleSet.rules[item.number - 1].conditions, ruleSet.rules[item.number - 1].conditionLogic)?.text ?? ""})`).join("; ")}`,
            expr: includes.length === 1 ? includes[0].expr.bool : `(${includes.map((item) => item.expr.bool).join(" OR ")})`,
            detail: `CONCAT(${nLiteral(`Rule set "${name}": no include rule matched: `)}, ${includes.map((item) => `N'[rule ${item.number}] ', ${item.expr.fail}`).join(", N' | ', ")})`,
        });
    }
    if (ruleSet.includeElse === false && matchedSets.length) {
        result.push({
            label: `Rule set "${name}": no matching rule (unmatched items are hidden)`,
            english: `it matches none of the categories of rule set "${name}" (rows without a category are hidden): ${ruleSet.rules
                .filter((rule) => rule.action === "set" && ruleHasResult(context, report, rule, modelKey))
                .map((rule) => ({ value: ruleResultLabel(context, report, rule), fromColumn: ruleValueFromColumn(rule), text: englishSetRule(context, report, rule, modelKey) }))
                .filter((item) => item.text)
                .map((item) => item.fromColumn ? `${item.value} when ${item.text}` : `"${item.value}" when ${item.text}`)
                .join("; ")}`,
            expr: matchedSets.length === 1 ? matchedSets[0].expr.bool : `(${matchedSets.map((item) => item.expr.bool).join(" OR ")})`,
            detail: `CONCAT(${nLiteral(`Rule set "${name}": no categorization rule matched: `)}, ${matchedSets.map((item) => `N'[', ${nLiteral(item.value)}, N'] ', ${item.expr.fail}`).join(", N' | ', ")})`,
        });
    }
    excludes.forEach((item) => result.push({
        label: `Rule set "${name}": matched an exclude rule`,
        english: `it matches exclude rule ${item.number} of rule set "${name}": ${englishNodes(context, report, ruleSet.rules[item.number - 1].conditions, ruleSet.rules[item.number - 1].conditionLogic)?.text ?? ""}`,
        expr: `NOT (${item.expr.bool})`,
        detail: `CONCAT(${nLiteral(`Rule set "${name}", exclude rule ${item.number}: `)}, ${item.expr.match})`,
    }));
    return result;
}

/** One side of a metric's filters with details, mirroring metricFilterClause. */
export function metricFilterExplainedCondition(context: ReportLogicContext, report: ReportConfig, section: MetricSection, mode: "filter" | "excludeFilter", useIds = false): ExplainedCondition | null {
    const nodes = metricFilterNodes(section, mode);
    if (!nodes.length) return null;
    const tree = explainNodes(context, metricFilterScope(report, section), nodes, metricFilterLogic(section, mode), useIds);
    if (!tree) return null;
    const english = englishNodes(context, metricFilterScope(report, section), nodes, metricFilterLogic(section, mode))?.text ?? "";
    return mode === "filter"
        ? { label: "Did not match the metric filter (only include rows where)", expr: tree.bool, detail: tree.fail, english: `it does not meet the metric's "only include rows where" filter: ${english}` }
        : { label: "Matched the metric filter (leave out rows where)", expr: `(CASE WHEN ${tree.bool} THEN 1 ELSE 0 END) = 0`, detail: tree.match, english: `it meets the metric's "leave out rows where" filter: ${english}` };
}

/** Trip Data include/exclude group with details, mirroring tripDataGroupExpr. */
export function tripDataExplained(context: ReportLogicContext, report: ReportConfig, modelKey: string, groupKey: "include" | "exclude", useIds = false): ExplainedExpr | null {
    if (modelKey !== "transportation") return null;
    const columns = columnsForModel(context, modelKey as any);
    const group = report.tripDataFilters[groupKey];
    const members = TRIP_DATA_FIELDS.map((field: TripDataFieldDef) => {
        const state = group.fields[field.key] as TripDataFieldState | undefined;
        const column = tripDataColumnForKey(context, field.column, columns);
        const expr = state && column ? tripDataFieldMatchExpr(context, column, field, state, modelKey as any, useIds) : null;
        if (!expr || !column || !state) return null;
        const values = state.values.map((value) => value.trim()).filter(Boolean);
        if (field.hasText && state.freeText.trim()) values.push(state.freeText.trim());
        const parts: string[] = [];
        if (values.length) parts.push(`one of: ${valueList(Array.from(new Set(values)))}`);
        if (field.hasText && state.contains.trim()) parts.push(`contains '${state.contains.trim()}'`);
        const rule = parts.join(" or ");
        return { bool: expr, fail: neededText(rowEquals(column), rule), match: excludedText(rowEquals(column), rule) } as ExplainedExpr;
    }).filter(Boolean) as ExplainedExpr[];
    if (!members.length) return null;
    if (members.length === 1) return members[0];
    return group.logic === "or" ? orLevel(members) : andRun(members);
}

/** Trip Data filter in plain English, mirroring EnglishTripData. */
export function tripDataEnglish(context: ReportLogicContext, report: ReportConfig, modelKey: string, groupKey: "include" | "exclude") {
    if (modelKey !== "transportation") return null;
    const columns = columnsForModel(context, modelKey as any);
    const group = report.tripDataFilters[groupKey];
    const parts = TRIP_DATA_FIELDS.map((field: TripDataFieldDef) => {
        const state = group.fields[field.key] as TripDataFieldState | undefined;
        const column = tripDataColumnForKey(context, field.column, columns);
        if (!state || !column) return null;
        const values = state.values.map((value) => value.trim()).filter(Boolean);
        if (field.hasText && state.freeText.trim()) values.push(state.freeText.trim());
        const bits: string[] = [];
        if (values.length) bits.push(englishPhrase("in", Array.from(new Set(values)), null, column));
        if (field.hasText && state.contains.trim()) bits.push(`contains "${state.contains.trim()}"`);
        return bits.length ? `${columnLabel(column)} ${bits.join(" or ")}` : null;
    }).filter(Boolean) as string[];
    if (!parts.length) return null;
    const joined = parts.join(group.logic === "or" ? ", or " : " and ");
    return groupKey === "include"
        ? `it does not match the report's Trip Data include filter: ${joined}`
        : `it matches the report's Trip Data exclude filter: ${joined}`;
}

export function breakdownListDetail(column: DataColumn, values: string[], included: boolean) {
    const rule = `one of: ${valueList(values)}`;
    return included ? neededText(rowEquals(column), rule) : excludedText(rowEquals(column), rule);
}

export function valueDetail(column: DataColumn, listName: "members" | "invoices") {
    return `CONCAT(${rowEquals(column)}, N' (on the report''s excluded ${listName} list)')`;
}
